// ─── Fiori Bridge — Aone-named components, Fiori bodies ──────────────────────
//
// The design system has two implementations: the editorial `components.js`
// (rounded cards, pill chips, tinted variants) and `fiori.js` (SAP Fiori
// geometry — 4px radii, hairline greys, semantic status colours), both drawing
// their palette from tokens.js, so the brand colours are identical either way.
//
// Rather than edit 260+ pages to swap imports, the public barrel (index.js)
// re-exports these wrappers under the Aone names. Each one normalises the prop
// differences between the two APIs so existing call sites keep working:
//
//   • AonePageShell  — `alert`, `footerBar`, `aiInsight` slots Fiori lacks
//   • AoneTabs       — accepts {key|value} tabs and active/activeTab handlers
//   • AoneEmptyState — `subtitle` alias, {label,onClick} action objects
//   • AoneKpiCard    — `variant` names mapped to semantic tones
//   • AoneInput      — `prefix` / `suffix` adornments
//
// To revert the whole ERP to the editorial look, point index.js back at
// './components'. Nothing else changes.
import React from 'react';
import { fieldLabel } from './fieldLabel';
import {
  FIORI as F, FioriPage, FioriCard, FioriKpiCard, FioriStatus, FioriDataTable,
  FioriTabBar, FioriActionBar, FioriRightPanel, FioriEmptyState, FioriAlertBanner,
  FioriButton, FioriInput, FioriSelect, FioriSectionHeader, FioriDivider,
  FioriSpinner, FioriToastStack, fioriFieldBase, FioriSheet, FioriSheetField } from './fiori';

const merge = (...s) => Object.assign({}, ...s.filter(Boolean));

// ── Page shell ───────────────────────────────────────────────────────────────
// Fiori has no alert / footer / AI slots; render them around the page body so
// pages that use them keep their content instead of silently dropping it.
export function AonePageShell({
  title, subtitle, breadcrumb, breadcrumbs, actions, alert, children, noPad, style,
  iconTile, status, meta, kpis, footerBar, aiInsight, mobileFab,
}) {
  // Ten pages call this `breadcrumbs` (the name the older components/aone shell
  // uses). React drops unknown props without complaint, so those pages rendered
  // no breadcrumb trail at all and nothing said why. Accept both spellings —
  // same reason AoneTabs below normalises active/activeTab.
  breadcrumb = breadcrumb || breadcrumbs;
  // footerBar retired 2026-07-30 (duplicated header KPIs/actions, ate
  // viewport height) — prop accepted and ignored so old callers don't crash.
  void footerBar;

  // Its KPI descriptors carry a separate `unit`. A short one ("L", "kg") folds
  // onto the value; a phrase ("purchase orders") is a LABEL for the mobile
  // headline and folding it gave "4purchase orders". Keep the raw value too,
  // so the headline can tell a zero from a formatted string.
  const kpiList = kpis?.map((k) => {
    if (!k) return k;
    const short = k.unit && String(k.unit).length <= 3;
    return {
      ...k,
      rawValue: k.value,
      value: short ? `${k.value}${k.unit}` : k.value,
      unitLabel: short ? undefined : k.unit,
      alarm: k.alarm ?? (k.tone === '#991B1B' || k.role === 'coral' || k.role === 'blush'),
    };
  });

  return (
    <FioriPage
      title={title} subtitle={subtitle} breadcrumb={breadcrumb} actions={actions}
      iconTile={iconTile} status={status} meta={meta} kpis={kpiList} mobileFab={mobileFab}
      style={noPad ? merge({ padding: 0 }, style) : style}
    >
      {alert && <div style={{ marginBottom: 10 }}>{alert}</div>}
      {aiInsight && <div style={{ marginBottom: 10 }}>{aiInsight}</div>}
      {children}
    </FioriPage>
  );
}

// ── Card ─────────────────────────────────────────────────────────────────────
// Fiori is flat: the `variant` tints collapse to the standard paper card.
export function AoneCard({ children, title, subtitle, headerRight, actions, variant, padding, onClick, style, bodyStyle, accentTop }) {
  return (
    <FioriCard
      title={title} subtitle={subtitle} headerRight={headerRight || actions}
      padding={padding} onClick={onClick} style={style} bodyStyle={bodyStyle}
      accentTop={accentTop}
    >{children}</FioriCard>
  );
}

// ── KPI card ─────────────────────────────────────────────────────────────────
const VARIANT_TONE = {
  mint: F.ok, sage: F.ok, aqua: F.ok,
  peach: F.warn, apricot: F.warn, gold: F.warn, sand: F.warn,
  coral: F.bad, rose: F.bad, blush: F.bad, terra: F.bad,
  sky: F.blue, lilac: F.blue, lavender: F.blue, oud: F.ink,
};
// `sub`/`trend`/`footnote`/`hint` are four names for the same thing: a line of
// context under the value ("22 with UE data", "vs last month"). None was a prop
// here, so ~33 call sites rendered a bare number and lost the line that made it
// mean something. They funnel into one `sub` slot on the tile below.
// Attention queues pass tone="critical"/"warning"/"positive"; FioriKpiCard
// colours from `color`, which wants a CSS value. Passing the semantic name
// straight through yielded an invalid colour, so every tile rendered plain
// ink and the tile that was meant to read as red simply did not.
const KPI_TONE = { critical: F.bad, negative: F.bad, warning: F.warn, positive: F.ok, ok: F.ok, info: F.blue };
export function AoneKpiCard({ label, value, delta, deltaLabel, icon, color, variant, onClick, style,
  sub, trend, footnote, hint, tone }) {
  color = color || (tone ? (KPI_TONE[tone] || tone) : undefined);
  return (
    <FioriKpiCard
      label={label} value={value} delta={delta} deltaLabel={deltaLabel} icon={icon}
      sub={sub || trend || footnote || hint}
      color={color || VARIANT_TONE[variant]} onClick={onClick} style={style}
    />
  );
}

// A KPI ribbon is just a responsive row of KPI cards.
export function AoneKpiRibbon({ kpis = [], style }) {
  return (
    <div style={merge({ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }, style)}>
      {kpis.map((k, i) => <AoneKpiCard key={k.label || i} {...k} />)}
    </div>
  );
}

// ── Status chip ──────────────────────────────────────────────────────────────
// `resolveStatus` semantics live inside FioriStatus via fioriState().
export function AoneStatusChip({ status, label, size = 'md', style }) {
  return <FioriStatus status={status} label={label} size={size} style={style} />;
}

// ── Data table ───────────────────────────────────────────────────────────────
export function AoneDataTable(props) {
  const { hoverVariant, ...rest } = props; // hover tint is fixed in Fiori
  return <FioriDataTable {...rest} />;
}

// ── Tabs ─────────────────────────────────────────────────────────────────────
// Call sites use either {key} or {value} tab ids, and either active/onChange or
// activeTab/onTabChange. Normalise both so no page has to care.
export function AoneTabs({ tabs = [], active, activeTab, value, onChange, onTabChange, style }) {
  const normalised = tabs.map(t => ({ ...t, key: t.key ?? t.value }));
  // `value` is the third spelling in use. Without it `current` was undefined, so
  // clicking a tab still worked but none ever rendered as selected.
  const current = active ?? activeTab ?? value;
  const handler = onChange || onTabChange || (() => {});
  return <FioriTabBar tabs={normalised} active={current} onChange={handler} style={merge({ border: `1px solid ${F.border}`, borderRadius: F.rCard, boxShadow: F.shadow }, style)} />;
}

// ── Action bar ───────────────────────────────────────────────────────────────
// `sticky` is dropped — a Fiori toolbar sits in the flow under the page header.
export function AoneActionBar({ sticky, style, ...rest }) {
  return <FioriActionBar {...rest} style={merge({ marginBottom: 12 }, style)} />;
}

// ── Sheet ────────────────────────────────────────────────────────────────────
export function AoneSheet(props) { return <FioriSheet {...props} />; }
export function AoneSheetField(props) { return <FioriSheetField {...props} />; }

// ── Right panel ──────────────────────────────────────────────────────────────
export function AoneRightPanel(props) {
  return <FioriRightPanel {...props} />;
}

// ── Empty state ──────────────────────────────────────────────────────────────
// `description`/`body` are the other two names call sites use for `message` —
// 33 of them, each rendering a bare title with the explanatory line silently
// dropped. Same defect as the SKU Alias Map table: React discards props a
// component doesn't declare, so the page looks merely sparse rather than broken.
export function AoneEmptyState({ icon, title = 'Nothing here', message, description, body, subtitle, action, inline, style }) {
  message = message || description || body;
  const node = (action && !React.isValidElement(action) && action.label)
    ? <FioriButton variant="secondary" size="sm" onClick={action.onClick}>{action.label}</FioriButton>
    : action;
  return (
    <FioriEmptyState
      icon={icon} title={title} message={message ?? subtitle} action={node}
      style={inline ? merge({ border: 'none', boxShadow: 'none', padding: '24px 12px' }, style) : style}
    />
  );
}

// ── Data state ───────────────────────────────────────────────────────────────
// One component for the four states a data panel can be in, so a page never
// renders a blank area for "failed" or a fresh-looking card for "stale".
//   <AoneDataState loading /> · <AoneDataState error="…" onRetry={fn} />
//   <AoneDataState empty title="…" /> · <AoneDataState stale="2h" />  (badge)
// Children render when none of the states apply, so it can wrap content.
export function AoneDataState({ loading, error, empty, stale, asOf, title, message, onRetry, inline, children, style }) {
  if (loading) {
    return (
      <div role="status" aria-live="polite" style={merge({ display: 'flex', alignItems: 'center', gap: 10, padding: inline ? '8px 0' : 24, color: 'var(--aone-text-sec, #6b7280)', fontSize: 13 }, style)}>
        <FioriSpinner size="sm" /> <span>{title || 'Loading…'}</span>
      </div>
    );
  }
  if (error) {
    const text = typeof error === 'string' ? error : (error?.message || 'Could not load this');
    return (
      <div role="alert" style={merge({ padding: inline ? '10px 12px' : 16, border: '1px solid #F4B4B4', background: '#FFF7F7', borderRadius: 8, fontSize: 13 }, style)}>
        <div style={{ fontWeight: 800, color: '#B42318' }}>{title || 'Failed to load'}</div>
        <div style={{ color: '#7A271A', marginTop: 4 }}>{text}</div>
        {onRetry && <div style={{ marginTop: 8 }}><FioriButton variant="secondary" size="sm" onClick={onRetry}>Retry</FioriButton></div>}
      </div>
    );
  }
  if (empty) {
    return <AoneEmptyState title={title || 'Nothing here yet'} message={message} inline={inline} style={style} />;
  }
  if (stale || asOf) {
    const label = stale === true ? 'Stale data' : stale ? `Stale · ${stale}` : null;
    return (
      <div style={style}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: stale ? '#A16207' : 'var(--aone-text-sec, #6b7280)', background: stale ? '#FEF9C3' : 'transparent', borderRadius: 20, padding: stale ? '2px 8px' : 0, marginBottom: 6 }}>
          {label || ''}{asOf ? `${label ? ' · ' : ''}as of ${new Date(asOf).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}` : ''}
        </div>
        {children}
      </div>
    );
  }
  return children ?? null;
}

// ── Alert banner ─────────────────────────────────────────────────────────────
// `type` is what 15 call sites pass for `variant`, and `onClose` what 8 pass
// for `onDismiss` — same story as breadcrumbs/description below. React drops
// both in silence, so a `type="error"` banner rendered as a neutral
// information strip (FioriAlertBanner falls back to 'info'), and a dismiss
// button called nothing. The values already match the vocabulary
// FioriAlertBanner understands; only the prop name was wrong.
export function AoneAlertBanner({ type, onClose, variant, onDismiss, ...rest }) {
  return <FioriAlertBanner variant={variant || type} onDismiss={onDismiss || onClose} {...rest} />;
}

// ── Primitives ───────────────────────────────────────────────────────────────
// `label` is the other spelling of children. Twelve call sites use it, and a
// FioriButton with no children is a button with no text on it.
export function AoneButton({ label, children, ...rest }) {
  return <FioriButton {...rest}>{children ?? label}</FioriButton>;
}

// FioriInput has no prefix/suffix adornments; wrap the field when one is asked
// for so currency and unit affixes keep rendering.
export function AoneInput({ prefix, suffix, ...rest }) {
  if (!prefix && !suffix) return <FioriInput {...rest} />;
  const { label, required, hint, error, style, inputStyle, ...field } = rest;
  const affix = { fontSize: 12, color: F.sub, flexShrink: 0 };
  const { text: labelText, showStar } = fieldLabel(label, required);
  return (
    <div style={merge({ display: 'flex', flexDirection: 'column', gap: 3, fontFamily: F.font }, style)}>
      {label && (
        <label style={{ fontSize: 12, color: F.sub }}>
          {labelText}{showStar && <span style={{ color: F.bad, marginLeft: 2 }}>*</span>}
        </label>
      )}
      <div style={merge(fioriFieldBase, { display: 'flex', alignItems: 'center', gap: 6, padding: '0 10px' })}>
        {prefix && <span style={affix}>{prefix}</span>}
        <input
          {...field}
          value={field.value ?? ''}
          onChange={e => field.onChange?.(e.target.value)}
          style={merge({ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', color: F.ink, fontFamily: F.font, fontSize: 13, padding: '5px 0' }, inputStyle)}
        />
        {suffix && <span style={affix}>{suffix}</span>}
      </div>
      {error ? <span style={{ fontSize: 11, color: F.bad }}>{error}</span>
        : hint ? <span style={{ fontSize: 11, color: F.muted }}>{hint}</span> : null}
    </div>
  );
}

export function AoneSelect(props) {
  return <FioriSelect {...props} />;
}

export function AoneSectionHeader({ title, subtitle, action, actions, style }) {
  return <FioriSectionHeader title={title} subtitle={subtitle} actions={action || actions} style={merge({ marginBottom: 12 }, style)} />;
}

export function AoneDivider(props) {
  return <FioriDivider {...props} />;
}

export function AoneSpinner(props) {
  return <FioriSpinner {...props} />;
}

export function AoneToastStack(props) {
  return <FioriToastStack {...props} />;
}

// ── Filter chip ──────────────────────────────────────────────────────────────
// Fiori's equivalent of a filter pill is a selectable segmented button.
export function AoneFilterChip({ active, onClick, role, children, style }) {
  return (
    <button onClick={onClick} style={merge({
      padding: '4px 12px', borderRadius: F.r, cursor: 'pointer',
      border: `1px solid ${active ? F.blue : F.borderStrong}`,
      background: active ? F.blue : F.surface,
      color: active ? '#fff' : F.ink,
      fontFamily: F.font, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
    }, style)}>{children}</button>
  );
}
